import { computed, inject, Injectable } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FeatureConfigurationApi } from './feature-configuration.api';
import { ISportConfiguration } from './feature-configuration.model';

export interface ISportOption {
  id: number;
  name: string;
}

/**
 * Every sport's feature configuration (organization-scoped server-side), fetched once and
 * shared. Backs sport pickers that also need that sport's reference-system context —
 * `configurationForSport` avoids a second per-sport round trip.
 */
@Injectable({ providedIn: 'root' })
export class SportConfigurationStore {
  private readonly api = inject(FeatureConfigurationApi);

  private readonly _configurations = rxResource({
    stream: () => this.api.getSportConfigurations(),
  });

  private readonly configurations = computed<ISportConfiguration[]>(
    () => this._configurations.value()?.results ?? [],
  );

  readonly isLoading = computed(() => this._configurations.isLoading());
  readonly error = computed(() => this._configurations.error());

  readonly sports = computed<ISportOption[]>(() =>
    this.configurations()
      .map((config) => ({ id: config.feature.sport.id, name: config.feature.sport.name }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  );

  configurationForSport(sportId: number): ISportConfiguration | undefined {
    return this.configurations().find((config) => config.feature.sport.id === sportId);
  }
}
